import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';

interface EmailDisplayProps {
  emailId: string;
  fallback?: string;
  className?: string;
}

/**
 * EmailDisplay component
 * Securely displays email addresses by requesting a short-lived token from the backend
 * Prevents email harvesting by spam bots
 */
export const EmailDisplay: React.FC<EmailDisplayProps> = ({
  emailId,
  fallback = 'contact@blyrha.com',
  className = '',
}) => {
  const [email, setEmail] = useState<string>('Loading...');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const revealEmail = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Step 1: Request a token from the backend
        const tokenResponse = await apiClient.requestEmailToken(emailId);

        if (tokenResponse.error) {
          console.error('Failed to request email token:', tokenResponse.error);
          setEmail(fallback);
          setError(tokenResponse.error);
          return;
        }

        if (!tokenResponse.data?.token) {
          console.error('No token received from backend');
          setEmail(fallback);
          setError('Failed to retrieve email');
          return;
        }

        // Step 2: Use the token to reveal the email
        const emailResponse = await apiClient.revealEmail(tokenResponse.data.token);

        if (emailResponse.error) {
          console.error('Failed to reveal email:', emailResponse.error);
          setEmail(fallback);
          setError(emailResponse.error);
          return;
        }

        if (!emailResponse.data?.email) {
          console.error('No email received from backend');
          setEmail(fallback);
          setError('Failed to retrieve email');
          return;
        }

        setEmail(emailResponse.data.email);
      } catch (err) {
        console.error('Unexpected error revealing email:', err);
        setEmail(fallback);
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    revealEmail();
  }, [emailId, fallback]);

  return (
    <span
      className={className}
      title={error ? `Error: ${error}` : undefined}
      data-email-id={emailId}
    >
      {email}
    </span>
  );
};

export default EmailDisplay;
